import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Observable, BehaviorSubject, Subject, of } from 'rxjs';
import { LgvAPIService } from '../../core/services/lgv-api.service';
import { LgvSettingsService, LgvUserService, NotificationService } from '../../core/core.module';
import { LgvModel } from '../../data-models/longevity.model';
import { map, first, takeUntil, withLatestFrom, catchError } from 'rxjs/operators';
import { BusinessTypeId, BusinessTypeName, EmailCampaignDialogMode } from '../../modules/default/default.enum';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LgvCompanyService } from '../../core/services/lgv-company.service';
import { UserEditModalComponent } from '../user-edit-modal/user-edit-modal.component';
import { CompanyEditModalComponent } from '../company-edit-modal/company-edit-modal.component';
import { DisclaimersModalComponent } from '../disclaimers-modal/disclaimers-modal.component';
import { EmailCampainDialogComponent } from '../email-campain-dialog/email-campain-dialog.component';
import { BillingStatus } from '../user-profile/user-profile.component';
import { BillingModalComponent } from '../billing-modal/billing-modal.component';
import { SocialMediaEditModalComponent } from '../social-media-edit-modal/social-media-edit-modal.component';

@Component({
    selector: 'lgv-agent-onboarding-banner',
    templateUrl: './agent-onboarding-banner.component.html',
    styleUrls: ['./agent-onboarding-banner.component.scss'],
})

export class AgentOnboardingBanner implements OnInit, OnDestroy {
    campaignStatus: EmailCampaignDialogMode = EmailCampaignDialogMode.Initial;
    onboardingChecklist$ = new BehaviorSubject(null);
    onDestroy$ = new Subject();
    isREAgent$ = new BehaviorSubject(null);
    billingStatus = null;
    isEmailSetupCompleted = false;
    isOnboarding$ = this.lgvUserSvc.isOnboarding$;
    companyInfo = null;

    get isStartingMode$(): Observable<boolean> { return this.onboardingChecklist$.pipe(map(x => !!x && (!(!!x.hasCompleteAgentProfile) || !(!!x.hasCompleteCompanyProfile)))) }
    get isFinishMode$(): Observable<boolean> { return this.onboardingChecklist$.pipe(map(x => !!x && (!!x.hasCompleteAgentProfile && !!x.hasCompleteCompanyProfile))) }

    constructor(private dialog: MatDialog,
        public lgvUserSvc: LgvUserService,
        private cdr: ChangeDetectorRef,
        private notificationService: NotificationService,
        private router: Router,
        public lgvApiSvc: LgvAPIService,
        private companyService: LgvCompanyService) { }

    ngOnInit(): void {
        this.initOnboardingChecklist();
        this.checkAgentType();
        this.updateBillingStatus();
        this.checkEmailStatus();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(null);
        this.onDestroy$.complete();
        this.cdr.detach();
    }

    //#region Old Checklist Page

    initOnboardingChecklist() {
        this.onboardingChecklist$.next(false);
        this.lgvUserSvc.getOnboardingChecklist(this.lgvUserSvc.AgentID)
            .pipe(takeUntil(this.onDestroy$)).subscribe((checklist) => {
                this.onboardingChecklist$.next(checklist);
                this.lgvUserSvc.refreshOnboardingChecklistStatus(checklist);
            })
    }

    checkAgentType() {
        this.lgvUserSvc.AgentSS$.pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
            if (value?.company?.businessTypeName === BusinessTypeName.RealEstate) {
                this.isREAgent$.next(true);
                this.initializeCompany();
            } else {
                this.isREAgent$.next(false);
            }
        })
    }

    initializeCompany() {
        this.companyService.getById(this.lgvUserSvc.AgentSS$.value?.company?.id)
            .pipe(takeUntil(this.onDestroy$)).subscribe((company) => {
                this.companyInfo = company;
            });
    }

    checkEmailStatus() {
        this.lgvUserSvc.getEmailStatus(this.lgvUserSvc.AgentID).pipe(
            takeUntil(this.onDestroy$),
            withLatestFrom(this.onboardingChecklist$)
        ).subscribe(([statusObject, checklist]) => {
            if (statusObject === null) {
                this.campaignStatus = EmailCampaignDialogMode.Initial;
            } else if (statusObject.verified.status === false) {
                this.campaignStatus = EmailCampaignDialogMode.Resend;
            } else if (statusObject.verified.status === true) {
                this.isEmailSetupCompleted = true;
            }
            this.cdr.detectChanges();
            this.initOnboardingChecklist();
        })
    }

    setupProfile() {
        const dialogRef = this.dialog.open(UserEditModalComponent, {
            width: '500px',
            data: {
                userInfo: this.lgvUserSvc.AgentSS$.value.agentInfo,
                isRoleHidden: true,
                isFromOnboarding: true,
                isREAgent: this.isREAgent$.value
            }
        });

        dialogRef.afterClosed().pipe().subscribe((data) => {
            if (data.action) {
                this.initOnboardingChecklist();
                this.lgvUserSvc.LoadAgentSS(this.lgvUserSvc.AgentID);
            }
        })
    }

    setupCompany() {
        const dialogRef = this.dialog.open(CompanyEditModalComponent, {
            width: '500px',
            data: {
                companyInfo: {
                    ...this.companyInfo
                },
                isFromOnboarding: true
            }
        });

        dialogRef.afterClosed().pipe().subscribe((data) => {
            if (data.action) {
                this.initOnboardingChecklist();
                this.lgvUserSvc.LoadAgentSS(this.lgvUserSvc.AgentID);
            }
        })
    }

    setupDisclaimers() {
        const dialogRef = this.dialog.open(DisclaimersModalComponent, {
            width: '500px',
            data: {
                companyInfo: this.companyInfo
            }
        });

        dialogRef.afterClosed().pipe().subscribe((data) => {
            if (data.action) {
                this.initOnboardingChecklist();
                this.initializeCompany();
            }
        })
    }

    setupEmail(emailStatus, companyStatus) {
        const dialogRef = this.dialog.open(EmailCampainDialogComponent, {
            width: '800px',
            data: {
                isFirstTimeEmail: true,
                mode: this.campaignStatus
            }
        });

        dialogRef.afterClosed().pipe().subscribe((data) => {
            if (data.dialogMode !== null && data.dialogMode !== EmailCampaignDialogMode.Initial) {
                if (data.dialogMode === EmailCampaignDialogMode.CheckEmailStatus) {
                    this.campaignStatus = EmailCampaignDialogMode.Resend;
                    this.checkEmailStatus();
                    return;
                }
                else {
                    this.campaignStatus = data.dialogMode;
                }

            }
            this.initOnboardingChecklist();
        })
    }

    setupPayment() {
        if (this.billingStatus === BillingStatus.NoAccount) {
            this.createBillingAccount();
        } else if (this.billingStatus === BillingStatus.NoPaymentMethod) {
            this.showBillingModal('manage-payment-sources');
        } else if (this.billingStatus === BillingStatus.Good) {
            this.showBillingModal('portal_sessions');
        }
    }

    createBillingAccount() {
        const agent = this.lgvUserSvc.AgentSS$.value?.agentInfo;
        const company = this.lgvUserSvc.AgentSS$.value?.company;

        this.lgvUserSvc.createBillingAccount(agent, company, this.lgvUserSvc.AgentID).pipe(
            takeUntil(this.onDestroy$),
            catchError((e) => {
                this.notificationService.error('Unable to create billing account');
                return of(null)
            })
        ).subscribe(response => {
            if (response === null) {
                return;
            }
            this.notificationService.success('Billing account has been created');
            this.showBillingModal('manage-payment-sources');
            this.billingStatus = BillingStatus.NoPaymentMethod;
        });
    }

    showBillingModal(type) {
        this.lgvUserSvc.startBillingPortalSession(this.lgvUserSvc.AgentID, type).pipe(
            takeUntil(this.onDestroy$),
            catchError((e) => {
                this.notificationService.error('Unable to start portal session with Billing Provider');
                return of(null);
            })
        ).subscribe(portalSessionResponse => {
            if (portalSessionResponse === null) {
                return;
            }
            const dialogRef = this.dialog.open(BillingModalComponent, {
                width: '400px',
                panelClass: 'billing-modal',
                disableClose: true,
                data: {
                    userInfo: this.lgvUserSvc.AgentSS$.value?.agentInfo,
                    iFrameUrl: portalSessionResponse.url
                }
            });
            dialogRef.afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe(x => {
                this.updateBillingStatus();
                this.initOnboardingChecklist();
            });
        });
    }

    updateBillingStatus() {
        this.lgvUserSvc.updateBillingStatus(this.lgvUserSvc.AgentID).pipe(
            first(),
            catchError((e) => {
                //this.notificationService.error('There was an error loading the billing information');
                return of(null);
            })
        ).subscribe((x) => {
            if (x === null) {
                this.billingStatus = BillingStatus.NoAccount;
                return;
            }
            const cardStatus: string = x?.customer?.cardStatus;
            this.billingStatus = (!!cardStatus && cardStatus === 'valid') ?
                BillingStatus.Good : BillingStatus.NoPaymentMethod;
        });
    }

    setupAgentSocials() {
        const dialogRef = this.dialog.open(SocialMediaEditModalComponent, {
            width: '500px',
            data: {
                agentInfo: this.lgvUserSvc.AgentSS$.value?.agentInfo
            }
        });

        dialogRef.afterClosed().pipe().subscribe((data) => {
            if (data.action) {
                this.initOnboardingChecklist();
                this.lgvUserSvc.LoadAgentSS(this.lgvUserSvc.AgentID);
            }
        })
    }

    setupCompanySocials() {
        const dialogRef = this.dialog.open(SocialMediaEditModalComponent, {
            width: '500px',
            data: {
                companyInfo: {
                    ...this.companyInfo,
                    businessTypeId: BusinessTypeId[this.companyInfo?.businessTypeName]
                }
            }
        });

        dialogRef.afterClosed().pipe().subscribe((data) => {
            if (data.action) {
                this.initOnboardingChecklist();
                this.initializeCompany();
            }
        })
    }

    goToUploadClients() {

        if (this.isOnboarding$?.value) {
            return;
        } else {
            this.router.navigate(['/admin/create-client', '']);
        }
    }

    //#endregion Old Checklist Page

}
